import { useCallback, useLayoutEffect, useMemo, useState, useSyncExternalStore } from "react";

import type { IdentifiedWindowEntry } from "contexts/windows";

import { DOCK_MINIMIZED_HEIGHT, DOCK_PANEL_GAP, DOCK_SELF_COLLAPSE_TOLERANCE } from "./constants";
import { useDockDragController } from "./dockDragContext";

export type DockSlotLayout =
    | { id: string; top: number; height: number; isPlaceholder: false; entry: IdentifiedWindowEntry }
    | { id: string; top: number; height: number; isPlaceholder: true; entry: null };

/** Drag-time placeholder slot, sized as equal-flex, following the cursor's insertion index. */
export interface DockPhantom {
    id: string;
    /** Real-slot index (0..dockedWindows.length). */
    index: number;
}

export interface UseDockLayoutResult {
    setZoneRef: (el: HTMLDivElement | null) => void;
    slots: DockSlotLayout[];
    /** Dock zone bounding rect in viewport coords; null until measured. */
    zoneRect: DOMRect | null;
}

/**
 * Per-slot heights: minimized = const, pinned = stored, rest split leftover space.
 *
 * When `phantom` is set (drag-in or restack), an equal-flex placeholder is
 * spliced in at the cursor's insertion index and the real flex slots shrink
 * to share. The dock controller's live-resize channel overrides the affected
 * slot's `dockedHeight` so resize gestures don't dispatch per pointermove.
 */
export function useDockLayout(
    dockedWindows: IdentifiedWindowEntry[],
    phantom: DockPhantom | null = null,
): UseDockLayoutResult {
    const controller = useDockDragController();
    const liveResize = useSyncExternalStore(controller.subscribeLiveResize, controller.getLiveResize, () => null);

    // useState so the layout effect below re-runs whenever the portal attaches
    // or detaches the dock element (zoneRef.current would not trigger an effect).
    const [zoneEl, setZoneEl] = useState<HTMLDivElement | null>(null);
    const [zoneRect, setZoneRect] = useState<DOMRect | null>(null);

    // Stable callback ref — no return value; cleanup lives in the effect below.
    // (React 18 callback refs must not return cleanup functions — that is a
    // React 19 feature only.)
    // Keep the last rect on detach to avoid a Maximum-Update-Depth loop when
    // the dock toggles visibility synchronously.
    const setZoneRef = useCallback((el: HTMLDivElement | null) => {
        setZoneEl(el);
        if (el) {
            setZoneRect(el.getBoundingClientRect());
        }
    }, []);

    // Re-run whenever the portal-backed element changes (null → element after
    // late attachment, element → null on unmount, or element → new element on
    // re-mount). Disconnects the previous observer before attaching a new one.
    useLayoutEffect(() => {
        if (!zoneEl || typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(() => {
            setZoneRect(zoneEl.getBoundingClientRect());
        });
        observer.observe(zoneEl);
        return () => observer.disconnect();
    }, [zoneEl]);

    const zoneH = zoneRect?.height ?? 0;

    const slots = useMemo<DockSlotLayout[]>(() => {
        const n = dockedWindows.length;
        if (n === 0 && !phantom) {
            return [];
        }

        // Live-resize override counts as fixed for both fixedTotal and flexCount.
        const getDockedHeight = (entry: IdentifiedWindowEntry): number | undefined =>
            liveResize?.id === entry.id ? liveResize.height : entry.state.dockedHeight;
        const getMinimizedHeight = (entry: IdentifiedWindowEntry): number =>
            entry.state.dockedMinimizedHeight ?? DOCK_MINIMIZED_HEIGHT;

        const phantomCount = phantom ? 1 : 0;
        const gapTotal = Math.max(0, n + phantomCount - 1) * DOCK_PANEL_GAP;

        // Classify slots. Minimized (measured/const) and pinned (resized) heights
        // are fixed; everything else starts as an equal-flex candidate.
        const PHANTOM_ID = "\u0000phantom";
        const fixedHeights = new Map<string, number>();
        const flexIds: string[] = [];
        for (const entry of dockedWindows) {
            if (entry.state.minimized) {
                fixedHeights.set(entry.id, getMinimizedHeight(entry));
            } else {
                const pinned = getDockedHeight(entry);
                if (pinned != null) {
                    fixedHeights.set(entry.id, pinned);
                } else {
                    flexIds.push(entry.id);
                }
            }
        }
        if (phantom) {
            flexIds.push(PHANTOM_ID);
        }

        // A third-party window can render far shorter than its equal-flex share
        // (it self-manages height). Pin any such window to its measured height so
        // the remaining flex windows reclaim the space instead of leaving a gap.
        // Each pass pins at least one window, so this converges.
        const contentHeightById = new Map<string, number | undefined>(
            dockedWindows.map((entry) => [entry.id, entry.state.dockedContentHeight]),
        );
        let flexPool = flexIds;
        let flexH = 0;
        for (;;) {
            const fixedTotal = [...fixedHeights.values()].reduce((sum, h) => sum + h, 0);
            flexH = flexPool.length > 0 ? Math.max(0, (zoneH - fixedTotal - gapTotal) / flexPool.length) : 0;
            const collapsedId = flexPool.find((id) => {
                const measured = contentHeightById.get(id);
                return measured != null && measured > 0 && measured < flexH - DOCK_SELF_COLLAPSE_TOLERANCE;
            });
            if (collapsedId == null) {
                break;
            }
            fixedHeights.set(collapsedId, contentHeightById.get(collapsedId) as number);
            flexPool = flexPool.filter((id) => id !== collapsedId);
        }

        const realSlots: DockSlotLayout[] = dockedWindows.map((entry) => {
            const height = fixedHeights.get(entry.id) ?? flexH;
            return { id: entry.id, top: 0, height, entry, isPlaceholder: false };
        });

        if (phantom) {
            const insertIdx = Math.min(Math.max(0, phantom.index), realSlots.length);
            realSlots.splice(insertIdx, 0, {
                id: `phantom:${phantom.id}`,
                top: 0,
                height: flexH,
                entry: null,
                isPlaceholder: true,
            });
        }

        let top = 0;
        for (const slot of realSlots) {
            slot.top = top;
            top += slot.height + DOCK_PANEL_GAP;
        }

        return realSlots;
    }, [dockedWindows, zoneH, phantom, liveResize]);

    return { setZoneRef, slots, zoneRect };
}
